import { ArtifactFormSkeleton } from "@/components/layout/artifact-form-skeleton"
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
} from "@/components/artifact/artifact-canvas-layout"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useCreateUser, useUpdateUser, useUser } from "@/hooks/use-admin-users"
import { toast } from "sonner"
import { Info } from "lucide-react"

const USER_FORM_ID = 'admin-user-form'

import { getPlatformAssignableGroups } from '@/lib/platform-assignable-groups'

interface UserFormProps {
  userId?: string
  onSuccess: () => void
  onCancel: () => void
  variant?: 'dialog' | 'canvas'
  /** When true, hide staff/superuser flags and limit groups to tenant front-facing roles. */
  platformMode?: boolean
}

export function UserForm({ userId, onSuccess, onCancel, variant = 'dialog', platformMode: _platformMode = false }: UserFormProps) {
  const isEdit = !!userId

  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [password, setPassword] = useState("")
  const [isActive, setIsActive] = useState(true)
  const [groupNames, setGroupNames] = useState<string[]>([])

  const { data: user, isLoading: isLoadingUser } = useUser(userId || "")
  const createUserMutation = useCreateUser()
  const updateUserMutation = useUpdateUser()

  // Load user data for editing
  useEffect(() => {
    if (isEdit && user) {
      setEmail(user.email)
      setFirstName(user.first_name || "")
      setLastName(user.last_name || "")
      setIsActive(user.is_active)
      setGroupNames(user.groups || [])
    }
  }, [isEdit, user])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const assignableNames = new Set(getPlatformAssignableGroups().map((group) => group.name))
    const userData = {
      email,
      first_name: firstName,
      last_name: lastName,
      is_active: isActive,
      group_names: groupNames.filter((name) => assignableNames.has(name)),
    }

    if (isEdit) {
      if (!userId) return

      updateUserMutation.mutate(
        { userId, data: userData },
        {
          onSuccess: () => {
            toast.success("User updated successfully")
            onSuccess()
          },
          onError: (error: Error) => {
            toast.error(`Failed to update user: ${error.message}`)
          }
        }
      )
    } else {
      createUserMutation.mutate(
        {
          ...userData,
          password: password || "defaultpassword123" // Fallback password
        },
        {
          onSuccess: () => {
            toast.success("User created successfully")
            onSuccess()
          },
          onError: (error: Error) => {
            toast.error(`Failed to create user: ${error.message}`)
          }
        }
      )
    }
  }

  const handleGroupChange = (groupName: string, checked: boolean) => {
    if (checked) {
      setGroupNames([...groupNames, groupName])
    } else {
      setGroupNames(groupNames.filter(g => g !== groupName))
    }
  }

  const isLoading = isLoadingUser || createUserMutation.isPending || updateUserMutation.isPending
  const isSaving = createUserMutation.isPending || updateUserMutation.isPending

  const assignableGroups = getPlatformAssignableGroups()
  const fallbackGroups = [
    { name: "employer", label: "Employer", description: "Can post jobs and manage applications" },
    { name: "recruiter", label: "Recruiter", description: "Can search candidates and manage briefs" },
    { name: "candidate", label: "Candidate", description: "Can apply for jobs and view applications" },
  ]
  const groupOptions = assignableGroups.length > 0 ? assignableGroups : fallbackGroups

  if (isEdit && isLoadingUser) {
    return (
      <ArtifactFormSkeleton fields={6} ariaLabel="Loading user form" />
    )
  }

  const formBody = (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Basic information</CardTitle>
          <CardDescription>
            User account details and contact information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              required
            />
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required={!isEdit}
              />
              <p className="text-sm text-muted-foreground">
                User will be prompted to change password on first login
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Permissions & status</CardTitle>
          <CardDescription>
            Account permissions and status settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setIsActive(checked === true)}
            />
            <Label htmlFor="isActive">Active account</Label>
          </div>

          <p className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 p-3 text-sm text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
            Access is group-based only. Operator groups (`developer`, `platform`) are assigned via deployment env.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Groups & roles</CardTitle>
          <CardDescription>
            Assign user to groups for role-based access control
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {groupOptions.map((group) => (
            <div key={group.name} className="flex items-center space-x-2">
              <Checkbox
                id={group.name}
                checked={groupNames.includes(group.name)}
                onCheckedChange={(checked) => handleGroupChange(group.name, checked as boolean)}
              />
              <div>
                <Label htmlFor={group.name} className="font-medium">
                  {group.label}
                </Label>
                <p className="text-sm text-muted-foreground">{group.description}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="size-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Profile Management</p>
              <p>Profile information (bio, location, skills, work experience) can be managed through the AI agent. Use the chat interface to update user profiles.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )

  if (variant === 'canvas') {
    return (
      <>
        <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
          <form id={USER_FORM_ID} onSubmit={handleSubmit} className="space-y-6">
            {formBody}
          </form>
        </ArtifactCanvasBody>
        <ArtifactCanvasFooterBar
          footerClassName="shrink-0"
          actions={
            <ArtifactCanvasSaveActions
              onClose={onCancel}
              onSave={() =>
                (document.getElementById(USER_FORM_ID) as HTMLFormElement | null)?.requestSubmit()
              }
              isDirty
              isSaving={isSaving}
              saveLabel="Update user"
              savingLabel="Saving…"
              closeDisabled={isSaving}
            />
          }
        />
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {formBody}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : (isEdit ? "Update user" : "Create user")}
        </Button>
      </div>
    </form>
  )
}
